import { PropsWithChildren, useMemo } from 'react';

import {
  CacheProvider,
  Global,
  ThemeProvider as EmotionThemeProvider,
} from '@emotion/react';
import { EmotionCache } from '@emotion/utils';
import { CssBaseline } from '@mui/material';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import { nonNullable } from '@scorenco/core';
import merge from 'deepmerge';

import { globals } from '../../style/globals';
import { createTheme } from '../createTheme';

const defaultTheme = createTheme();

type GlobalThemeProviderProps = PropsWithChildren<{
  theme?: typeof defaultTheme;
  cache: EmotionCache;
}>;

/**
 * Bridge MUI theme into Emotion's own ThemeContext so that the `css` prop
 * function `(theme) => ...` receives the full MUI theme (including `vars`).
 *
 * This is needed because Turbopack may resolve @emotion/react to different
 * ESM/CJS module instances for MUI's internal styled-engine vs the jsx-runtime,
 * causing separate ThemeContext objects. This bridge ensures the theme is set
 * in the same ThemeContext that the css prop reads from.
 */
const EmotionThemeBridge = ({ children }: PropsWithChildren) => {
  const theme = useTheme();

  return (
    <EmotionThemeProvider theme={theme}>
      <Global styles={globals(theme)} />
      <CssBaseline />
      {children}
    </EmotionThemeProvider>
  );
};

/**
 * Initialisation globale du theme, à utiliser à la création de l'application.
 */
export const GlobalThemeProvider = ({
  children,
  theme,
  cache,
}: GlobalThemeProviderProps) => {
  const mergedTheme = useMemo(
    () =>
      merge.all<typeof defaultTheme>([defaultTheme, theme].filter(nonNullable)),
    [theme]
  );

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={mergedTheme} defaultMode="light">
        <EmotionThemeBridge>{children}</EmotionThemeBridge>
      </ThemeProvider>
    </CacheProvider>
  );
};
